import { premierLien } from './liens'

/**
 * Ce qu'une application de téléphone nous envoie quand on partage vers nous.
 *
 * Android appelle l'application avec trois paramètres — `title`, `text`, `url` — et chacune les
 * remplit à sa façon. Instagram et Facebook ne renseignent <b>pas</b> `url` : ils glissent
 * l'adresse dans `text`, souvent précédée d'une phrase. S'en tenir à `url` ferait donc échouer
 * le partage sur précisément les deux applications d'où viennent les recettes.
 *
 * D'où cette lecture tolérante : on prend `url` s'il existe, sinon la première adresse trouvée
 * dans `text`, sinon dans `title`. Et ce qui reste du texte, une fois l'adresse retirée, sert
 * de note — c'est souvent la légende de la publication.
 */
export type PartageEntrant = {
  /** L'adresse partagée, si l'on en a trouvé une. */
  readonly lien: string | null
  /** Le titre proposé par l'application d'origine. */
  readonly titre: string | null
  /** Le texte accompagnant, débarrassé de l'adresse qu'on en a extraite. */
  readonly texte: string | null
}

export function partageEntrant(parametres: URLSearchParams): PartageEntrant {
  const titre = vide(parametres.get('title'))
  const texte = vide(parametres.get('text'))
  const adresse = vide(parametres.get('url'))

  const lien = adresse ?? premierLien(texte) ?? premierLien(titre)

  return {
    lien,
    // Un titre qui n'est que l'adresse n'apprend rien : la fiche le reprendrait tel quel.
    titre: titre === null || titre === lien ? null : titre,
    texte: sansLeLien(texte, adresse === null ? lien : null),
  }
}

/** Retire du texte l'adresse qu'on en a tirée : la répéter dans la note ne sert à rien. */
function sansLeLien(texte: string | null, lien: string | null): string | null {
  if (texte === null) {
    return null
  }

  return lien === null ? texte : vide(texte.split(lien).join(' ').replace(/\s+/gu, ' '))
}

function vide(valeur: string | null): string | null {
  const propre = (valeur ?? '').trim()

  return propre.length === 0 ? null : propre
}
